// src/lib/direct-messages-api.js — Direct A2 THIN, sous-lot 2 (VOIE A).
//
// Client admin LECTURE SEULE de la STRUCTURE du parcours réservation directe.
// Appelle /api/beds24-mapping?action=direct_messages_list (Bearer JWT admin,
// requireAdmin côté serveur). Renvoie les catégories + gabarits. Le texte PAR
// DÉFAUT (référence lecture seule) vient des builders codés via
// fetchDirectMessagesBuilders (action direct_messages_builders — M10, zéro KV).

import { supabase } from "../supabase";

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Non authentifié");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * Liste la structure du parcours Direct groupée par catégorie. Inclut le contenu
 * FR courant (subject_fr/body_fr) + is_edited pour réhydrater l'éditeur (sous-lot
 * 3A).
 * @returns {Promise<{ ok: boolean, categories: Array }>}
 */
export async function fetchDirectMessages() {
  const res = await fetch("/api/beds24-mapping?action=direct_messages_list", {
    headers: await authHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || "Lecture de la structure Direct échouée");
  }
  return json;
}

/**
 * M10 — défauts BUILDERS des gabarits Direct (lecture seule) : pour chaque
 * gabarit codé, { slug, label, group, bilingue, vars, default, default_tokenized }
 * + exampleVars (enrichies du nom de marque réel). Référence lecture seule de
 * l'éditeur — remplace fetchJourneyOverrides (éditeur KV déprécié).
 *   - `default` : builder rendu avec valeurs d'EXEMPLE (référence lecture seule).
 *   - `default_tokenized` (CORRECTION 1) : chaîne canonique à {tokens} posée dans
 *     l'éditeur quand is_edited=false (byte-identique au builder). null pour les
 *     gabarits composés (non tokenisables) → l'éditeur reste vide.
 * @returns {Promise<{ ok: boolean, items: Array, exampleVars: object }>}
 */
export async function fetchDirectMessagesBuilders() {
  const res = await fetch(
    "/api/beds24-mapping?action=direct_messages_builders",
    { headers: await authHeaders() },
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || "Lecture des textes par défaut échouée");
  }
  return json;
}

/**
 * LOT L1 — conditions RÉELLES du moteur Direct (lecture seule) : constantes du
 * sweep temporel + état DRY_RUN. Best-effort côté appelant : un échec ne doit
 * pas casser l'onglet (fallback descriptifs statiques).
 */
export async function fetchDirectConditions() {
  const res = await fetch("/api/beds24-mapping?action=direct_conditions_get", {
    headers: await authHeaders(),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || "Lecture des conditions moteur échouée");
  }
  return json;
}

// POST générique vers une action direct_messages_*. Remonte json.detail en
// priorité (message clair de validation, ex. variable inconnue) pour affichage
// direct à l'admin.
async function postAction(action, payload, fallback) {
  const res = await fetch(`/api/beds24-mapping?action=${action}`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.detail || json.error || fallback);
    err.code = json.error;
    err.unknown = json.unknown;
    throw err;
  }
  return json;
}

const postSave = (payload, fallback) =>
  postAction("direct_messages_save", payload, fallback);

/**
 * Enregistre le contenu d'un gabarit Direct (objet + corps FR), is_edited=true.
 * `name` (optionnel) : renommage — accepté serveur UNIQUEMENT pour un message
 * manuel (3B). `subject_en`/`body_en` (lot L) : messages bilingues uniquement,
 * contrat tout-ou-rien côté serveur (les deux ou aucun).
 * `subject_es`/`body_es` (B4-ext) : MÊME contrat que l'EN. Le texte ES est bien
 * ENREGISTRÉ ; son service à l'envoi (aujourd'hui traduction auto depuis le FR)
 * reste un lot séparé.
 * @param {string} slug
 * @param {{ subject_fr: string, body_fr: string, name?: string,
 *           subject_en?: string, body_en?: string,
 *           subject_es?: string, body_es?: string }} content
 */
export async function saveDirectMessage(slug, content) {
  const payload = {
    slug,
    op: "set",
    subject_fr: content.subject_fr,
    body_fr: content.body_fr,
  };
  if (content.name) payload.name = content.name;
  if (content.subject_en !== undefined) payload.subject_en = content.subject_en;
  if (content.body_en !== undefined) payload.body_en = content.body_en;
  if (content.subject_es !== undefined) payload.subject_es = content.subject_es;
  if (content.body_es !== undefined) payload.body_es = content.body_es;
  return postSave(payload, "Enregistrement du message échoué");
}

/**
 * Réinitialise un gabarit Direct au texte par défaut (builder) — efface le
 * contenu base et repose is_edited=false.
 * @param {string} slug
 */
export async function resetDirectMessage(slug) {
  return postSave({ slug, op: "reset" }, "Retour au défaut échoué");
}

/**
 * Crée un message MANUEL (3B) dans une catégorie : brouillon, slug généré côté
 * serveur, contenu vide. Renvoie { ok, message } (la ligne créée).
 * @param {string} categoryId
 * @param {string} name
 */
export async function createDirectMessage(categoryId, name) {
  return postAction(
    "direct_messages_create",
    { category_id: categoryId, name },
    "Création du message échouée",
  );
}

/**
 * Supprime un message MANUEL (3B). Les 14 gabarits d'origine sont refusés par
 * le serveur (garde-fou), l'UI ne propose pas le bouton pour eux.
 * @param {string} slug
 */
export async function deleteDirectMessage(slug) {
  return postAction(
    "direct_messages_delete",
    { slug },
    "Suppression du message échouée",
  );
}

/**
 * Réordonne les messages D'UNE catégorie (3B) : l'ensemble ordered_slugs doit
 * être exactement celui de la catégorie (intra-catégorie uniquement).
 * @param {string} categoryId
 * @param {string[]} orderedSlugs
 */
export async function reorderDirectMessages(categoryId, orderedSlugs) {
  return postAction(
    "direct_messages_reorder",
    { category_id: categoryId, ordered_slugs: orderedSlugs },
    "Réordonnancement échoué",
  );
}

/**
 * Règle le statut (actif/brouillon) et/ou les canaux d'un message (lot C).
 * DORMANT : stocké, aucun effet d'envoi tant que la bascule 'db' n'a pas eu lieu.
 * @param {string} slug
 * @param {{ status?: 'actif'|'brouillon', canal?: string[] }} settings
 */
export async function saveDirectMessageSettings(slug, settings) {
  return postAction(
    "direct_messages_settings",
    { slug, ...settings },
    "Réglage du message échoué",
  );
}
